import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api } from '~/api/client.js';
import { useAnonymous, useAuthStore, usePlayerId } from '~/store/authStore.js';
import { AVATAR_IDS, type AvatarId, FREE_AVATARS, isAvatarId, ownFace } from '~/store/avatars.js';
import { useEntitlement } from '~/store/membership.js';
import type { FriendlyError } from '~/store/sessionStore.js';
import { Avatar } from '~/ui/Avatar.js';
import { Banner } from '~/ui/Banner.js';
import { Button, ButtonLink } from '~/ui/Button.js';
import { LoadingLine } from '~/ui/LoadingLine.js';
import { Section } from '~/ui/Section.js';
import { useLatestRequest } from '~/ui/useLatestRequest.js';
import { LeanTag } from '~/table/classic/LeanTag.js';
import { ACCOUNT_PATH, accountPath, returnToChip } from './accountPages.js';
import { MemberGate, type OfferReturn, parkedPreview } from './MemberGate.js';
import { SubmitButton } from './SubmitButton.js';

/**
 * Change avatar: the faces an account may wear, the current one pressed, and
 * Save and Cancel under them. Drawn to the `lobby-account-settings` sketch
 * (state 5) and `docs/ux/account/avatar-catalog.md`.
 *
 * The four free faces come first, then the members' faces in their own band
 * (`docs/ux/account/avatar-catalog.md`: locked choices after available ones).
 * Each band is a named region, and each face is a pressed-or-not chip, so the
 * chosen one is the state a toggle already announces, and its name says
 * whether it is free, a member's, or members only.
 *
 * A Communications Pro member may wear any face. Anyone else may press a
 * member's face to look at it, wearing a Members tag, but it is not saved:
 * Save gives way to the members-only line, which names the face they keep and
 * offers the offer. Back from the offer, the face they asked about is pressed
 * again.
 *
 * Save returns to Account, which says the face was saved, with focus on the
 * chip that opened this page. A refusal keeps the face that was picked and
 * says which one did not save; Save is then the retry, and Back or Cancel
 * leaves. A guest meets the account wall instead, as the chip on Account
 * already sends them there.
 *
 * The page belongs to one player. Another signing in while a save is out
 * gives them a fresh page, with their own face pressed and nothing in the
 * air, and the old save's answer goes unreported.
 */
export function AvatarPage(): React.JSX.Element {
  const playerId = usePlayerId();
  return <AvatarChoice key={playerId ?? ''} />;
}

/** Every face a member may wear and a free player may not, in catalog order. */
const MEMBER_AVATARS: readonly AvatarId[] = AVATAR_IDS.filter((id) => !FREE_AVATARS.includes(id));

/** Where the offer's Back returns: this page. */
const AVATAR_PATH = accountPath('avatar');

function AvatarChoice(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const ready = useAuthStore((state) => state.ready);
  const anonymous = useAnonymous();
  const member = useEntitlement('comms');
  const arrival = useLocation().state as { returnTo?: unknown } | null;
  const offer: OfferReturn = {
    backTo: AVATAR_PATH,
    returnTo: typeof arrival?.returnTo === 'string' ? arrival.returnTo : undefined,
  };
  // Back from the offer, the face the player asked about is pressed again.
  const [picked, setPicked] = useState<AvatarId | null>(() => {
    const parked = parkedPreview('avatar', offer);
    return isAvatarId(parked) ? parked : null;
  });
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  // Leaving the page retires the save; the hook does that on unmount.
  const request = useLatestRequest();

  // A reload lands here before `/me` answers. Wait for it, as Delete does.
  if (user === null && !ready) return <LoadingLine />;
  if (anonymous || user === null) return <Navigate to={accountPath('need-avatar')} replace />;

  const current = ownFace(user.avatar_id);
  const choice = picked ?? current;
  const name = (id: AvatarId): string => t(`avatars.${id}`);
  const free = (id: AvatarId): boolean => FREE_AVATARS.includes(id);
  // A member's face on a free player is a preview: there is nothing to save.
  const locked = !member && !free(choice);

  const back = (): void => {
    void navigate(ACCOUNT_PATH, { replace: true, state: returnToChip('avatar') });
  };

  const save = async (): Promise<void> => {
    if (locked) return;
    if (choice === current) {
      back();
      return;
    }
    const isCurrent = request.begin();
    setBusy(true);
    setFailure(null);
    const refused = await useAuthStore.getState().saveMe(() => api.updateMe({ avatar_id: choice }));
    // Left, or signed in as somebody else, while the save was out: the answer
    // is not this page's to report, and "saved" would name the wrong face.
    if (!isCurrent()) return;
    if (refused !== null) {
      setFailure(refused);
      setBusy(false);
      return;
    }
    void navigate(ACCOUNT_PATH, {
      replace: true,
      state: { ...returnToChip('avatar'), flash: 'avatarSaved' },
    });
  };

  const pick = (id: AvatarId): void => {
    setPicked(id);
    setFailure(null);
  };

  return (
    <form
      className="flex flex-col gap-6"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) void save();
      }}
    >
      <p className="text-muted">{t('settings.account.avatarLead')}</p>
      <Section title={t('settings.account.avatarFaces')}>
        <div className="grid grid-cols-2 gap-3">
          {FREE_AVATARS.map((id) => (
            <Face
              key={id}
              id={id}
              label={t('settings.account.avatarOption', { name: name(id) })}
              pressed={choice === id}
              onPress={pick}
            />
          ))}
        </div>
      </Section>
      <Section title={t('settings.account.avatarMemberFaces')}>
        <div className="grid grid-cols-3 gap-3">
          {MEMBER_AVATARS.map((id) => (
            <Face
              key={id}
              id={id}
              label={t(
                member
                  ? 'settings.account.avatarOptionMember'
                  : 'settings.account.avatarOptionLocked',
                { name: name(id) },
              )}
              pressed={choice === id}
              onPress={pick}
              tag={member ? undefined : t('settings.paywall.members')}
            />
          ))}
        </div>
      </Section>
      {locked && (
        <MemberGate
          door="avatar"
          keeping={t('settings.paywall.keepingFace', { name: name(current) })}
          preview={choice}
          offer={offer}
        />
      )}
      {failure !== null && (
        <Banner role="alert">
          {t('settings.account.avatarNotSaved', { name: name(choice) })} {t(failure.key)}
        </Banner>
      )}
      <div className="flex justify-end gap-3">
        <ButtonLink variant="ghost" to={ACCOUNT_PATH} replace state={returnToChip('avatar')}>
          {t('common.cancel')}
        </ButtonLink>
        {!locked && (
          <SubmitButton busy={busy}>
            {failure === null ? t('settings.save') : t('common.retry')}
          </SubmitButton>
        )}
      </div>
    </form>
  );
}

/**
 * One face in the grid: a pressed-or-not chip, the face over its name. A
 * member's face on a free player wears the Members tag on its corner. It is
 * white, not the lemon the Game page's single tag wears: thirty-nine lemon
 * tags would drown the page's one lemon, Save (BRAND.md, color rule 1).
 */
function Face({
  id,
  label,
  pressed,
  onPress,
  tag,
}: {
  readonly id: AvatarId;
  readonly label: string;
  readonly pressed: boolean;
  readonly onPress: (id: AvatarId) => void;
  readonly tag?: string | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Button
      variant="subtle"
      aria-pressed={pressed}
      aria-label={label}
      onClick={() => {
        onPress(id);
      }}
    >
      <span className="flex flex-col items-center gap-2 py-2">
        <span className="relative">
          <Avatar kind="person" look={id} side="rows" size="band" outline="plain" />
          {tag !== undefined && <LeanTag label={tag} small placement="pinned" />}
        </span>
        <span>{t(`avatars.${id}`)}</span>
      </span>
    </Button>
  );
}
